import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Edit, Trash } from "@mynaui/icons-react";
import { FileText, Lock, GripVertical } from "lucide-react";
import { getFieldsByCategory, deleteCustomRegistrationField, reorderFields } from "../../api/customRegistrationFields";
import { getTournamentCategoryDetail } from "../../api/tournaments";
import { CustomRegistrationFieldDTO, FieldTypeLabels } from "../../models/ICustomRegistrationField";
import CustomRegistrationFieldDialog from "../../components/CustomRegistrationFieldDialog";
import DeleteCustomFieldDialog from "../../components/DeleteCustomFieldDialog";
import { Alert, Button, DescriptionItem, DescriptionList, EmptyState, IconButton, Loader, PageHeader, Tag } from "../../ui";
import "./styles.scss";

const CategoryCustomFieldsPage = () => {
  const { id, tournamentCategoryId } = useParams();
  const hasFetched = useRef(false);

  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState("");
  const [tournamentName, setTournamentName] = useState("");
  const [fields, setFields] = useState<CustomRegistrationFieldDTO[]>([]);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingField, setEditingField] = useState<CustomRegistrationFieldDTO | undefined>(undefined);
  const [deletingField, setDeletingField] = useState<{ id: string; name: string } | undefined>(undefined);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const fetchData = async () => {
    if (!id || !tournamentCategoryId) return;

    setLoading(true);
    try {
      const [categoryRes, fieldsRes] = await Promise.all([
        getTournamentCategoryDetail(tournamentCategoryId),
        getFieldsByCategory(tournamentCategoryId),
      ]);

      setTournamentName(categoryRes.data.tournamentName);
      setCategoryName(categoryRes.data.categoryName);
      setFields(fieldsRes.data || []);
      document.title = `Custom Fields - ${categoryRes.data.categoryName}`;
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tournamentCategoryId]);

  const handleAddClick = () => {
    setShowCreateDialog(true);
  };

  const handleDialogClose = (update?: boolean) => {
    setShowCreateDialog(false);
    setEditingField(undefined);
    if (update) {
      fetchData();
    }
  };

  const handleDeleteClose = (update?: boolean) => {
    setDeletingField(undefined);
    if (update) {
      fetchData();
    }
  };

  const handleEdit = (field: CustomRegistrationFieldDTO) => {
    setEditingField(field);
  };

  const handleDelete = (field: CustomRegistrationFieldDTO) => {
    setDeletingField({ id: field.id, name: field.fieldName });
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newFields = [...fields];
    const [draggedField] = newFields.splice(draggedIndex, 1);
    newFields.splice(index, 0, draggedField);
    setFields(newFields);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null || !tournamentCategoryId) return;

    setDraggedIndex(null);
    const fieldIds = fields.map((f) => f.id);

    try {
      await reorderFields(tournamentCategoryId, fieldIds);
    } catch (error) {
      console.error("Error reordering fields:", error);
      fetchData(); // Revert to server state on error
    }
  };

  const getFieldTypeIcon = () => {
    return <FileText size={20} className="field-type-icon" />;
  };

  const hasValue = (value?: number | null) => value !== undefined && value !== null;

  // The label / value pairs under a field's name, in the order they always showed
  const fieldDetails = (field: CustomRegistrationFieldDTO): DescriptionItem[] => {
    const items: DescriptionItem[] = [{ label: "Type", value: FieldTypeLabels[field.fieldType] }];
    if (field.placeholder) items.push({ label: "Placeholder", value: field.placeholder });
    if (field.helpText) items.push({ label: "Help Text", value: field.helpText, wide: true });
    const hasMinLength = hasValue(field.minLength) && field.minLength! > 0;
    if (hasMinLength || hasValue(field.maxLength)) {
      items.push({
        label: "Length",
        value: (
          <>
            {hasMinLength ? `Min: ${field.minLength}` : ""}
            {hasMinLength && hasValue(field.maxLength) ? " / " : ""}
            {hasValue(field.maxLength) ? `Max: ${field.maxLength}` : ""}
          </>
        ),
      });
    }
    if (hasValue(field.minValue) || hasValue(field.maxValue)) {
      items.push({
        label: "Value Range",
        value: (
          <>
            {hasValue(field.minValue) ? `Min: ${field.minValue}` : ""}
            {hasValue(field.minValue) && hasValue(field.maxValue) ? " / " : ""}
            {hasValue(field.maxValue) ? `Max: ${field.maxValue}` : ""}
          </>
        ),
      });
    }
    if (field.validationPattern) {
      items.push({ label: "Validation", value: field.validationMessage || field.validationPattern });
    }
    return items;
  };

  return (
    <div className="category-custom-fields-page">
      <PageHeader
        back={{ to: `/dashboard/tournaments/${id}/${tournamentCategoryId}`, label: "Back to Category" }}
        eyebrow={tournamentName}
        title={`${categoryName} - Custom Registration Fields`}
        subtitle={!loading ? `${fields.length} Custom ${fields.length === 1 ? "Field" : "Fields"}` : undefined}
        actions={
          !loading && fields.length > 0 ? (
            <Button icon={<Plus size={18} />} onClick={handleAddClick}>
              Add Custom Field
            </Button>
          ) : undefined
        }
      />

      <Alert tone="info" className="info-banner">
        Custom registration fields allow you to collect additional information from players during registration.
        Fields marked as encrypted are automatically secured for sensitive data like passport numbers.
      </Alert>

      {loading ? (
        <Loader />
      ) : fields.length === 0 ? (
        <EmptyState
          icon={<FileText />}
          title="No custom fields yet"
          description="Add custom fields to collect additional information during player registration"
          action={
            <Button icon={<Plus size={18} />} onClick={handleAddClick}>
              Add Your First Custom Field
            </Button>
          }
        />
      ) : (
        <div className="fields-list">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className={`field-card ${!field.isActive ? "inactive" : ""} ${draggedIndex === index ? "dragging" : ""}`}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div className="drag-handle">
                <GripVertical size={16} />
              </div>

              <div className="field-content">
                <div className="field-header">
                  <div className="field-info">
                    {getFieldTypeIcon()}
                    <div>
                      <div className="field-name-row">
                        <h3>{field.fieldName}</h3>
                        {field.isEncrypted && (
                          <Tag tone="warn" icon={<Lock size={12} />}>
                            Encrypted
                          </Tag>
                        )}
                        {field.isRequired && <Tag tone="danger">Required</Tag>}
                      </div>
                      <p className="field-key">{field.fieldKey}</p>
                    </div>
                  </div>
                  <div className="field-actions">
                    <IconButton label="Edit" icon={<Edit size={18} />} onClick={() => handleEdit(field)} />
                    <IconButton label="Delete" variant="danger" icon={<Trash size={18} />} onClick={() => handleDelete(field)} />
                  </div>
                </div>

                <DescriptionList className="field-details" columns={3} items={fieldDetails(field)} />

                <div className="field-footer">
                  <Tag tone={field.isActive ? "ok" : "neutral"}>{field.isActive ? "Active" : "Inactive"}</Tag>
                  <span className="display-order">Order: {field.displayOrder}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showCreateDialog || editingField) && (
        <CustomRegistrationFieldDialog
          tournamentCategoryMappingId={tournamentCategoryId!}
          field={editingField}
          isOpen={true}
          onClose={handleDialogClose}
        />
      )}

      {deletingField && (
        <DeleteCustomFieldDialog
          fieldId={deletingField.id}
          fieldName={deletingField.name}
          isOpen={true}
          onClose={handleDeleteClose}
        />
      )}
    </div>
  );
};

export default CategoryCustomFieldsPage;
